/*
  The full-width surface, for work a dialog was the wrong container for.

  WHAT A MODAL SAYS. A dialog is an interruption: it dims what you were doing, takes your focus, and
  asks to be dismissed before anything else happens. That is right for "pick a column", "confirm this
  delete", "edit these five assumptions". It is wrong for the TSA register — a table of agreements
  somebody works through for an hour — and it is wrong for six panes of steering-committee analytics.
  `docs/SEPARATION-REVIEW.md` names it as the clearest single signal that the information
  architecture was inherited rather than designed: the heaviest surfaces in the product were reached
  by opening a box on top of the thing they describe, and capped at 1000px in the middle of a screen
  with 1400 to give them.

  A WORKBENCH IS A PLACE, NOT AN INTERRUPTION. It replaces the canvas rather than covering it, it is
  as wide as the window, it has an address, and the back button leaves it. Nothing is dimmed because
  nothing is waiting on you.

  SAME INTERNAL CONTRACT AS `Modal`, DELIBERATELY: `<header>` with an `<h2>` and a Close button, a
  scrollable body, an optional footer, and `data-testid` in the same place. That is not laziness —
  the end-to-end suite closes these surfaces with `[data-testid="…"] header button` and reads their
  body's scroll position, and keeping the shape means moving four surfaces out of dialogs costs the
  suite one selector rather than a dozen. What changes is where the thing sits and what it does to
  the page around it, which is the entire point.

  NOT A DIALOG, SO NO FOCUS TRAP AND NO ESCAPE. Both would be wrong here: trapping focus in a place
  you have navigated to is how a page stops being navigable, and Escape means "cancel" — a reader
  three tabs into the register has nothing to cancel. It is a labelled `<section>`, which is a
  landmark, so a screen reader announces it and can jump to it.
*/
import type { ReactNode } from 'react';
import { ScopeNotice } from './ScopeNotice.js';

export function Workbench({
  testId,
  title,
  onClose,
  children,
  footer,
}: {
  /** On the surface, exactly where the dialog it replaced put it. */
  testId: string;
  title: string;
  /** Leaves the workbench and returns the canvas. Not a dismissal — a navigation. */
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
}): JSX.Element {
  /*
    The title element is addressed by id so the region carries an accessible name. Derived from the
    testid rather than the title text, because a title is prose — it has spaces, en-dashes and
    ampersands in it — and an id has to be one token.
  */
  const titleId = `${testId}-title`;
  return (
    <section className="workbench" data-testid={testId} aria-labelledby={titleId}>
      <header>
        <h2 id={titleId}>{title}</h2>
        {/*
          Says what it does. "Close" was right on a dialog and is not right here: this leaves a place
          and returns the chart, and a reader who has been in the register for ten minutes should be
          told which of those is about to happen. The `header button` selector the suite uses does not
          care about the word, and neither does anything else.
        */}
        {/*
          WHAT THIS READER IS NOT SEEING, in the shell rather than in each pane.

          The workbench is where the numbers are and there are twenty panes; one of them forgetting
          the notice is the failure this placement removes, because a pane cannot render without its
          shell. `ScopeNotice` reads the store itself and answers null when there is nothing to say,
          so this costs nothing on the ordinary path and cannot be omitted on the other one.
        */}
        <ScopeNotice where="workbench" />
        <button onClick={onClose}>Back to the chart</button>
      </header>
      <div className="workbench-body">{children}</div>
      {footer !== undefined && <footer>{footer}</footer>}
    </section>
  );
}
